import { useCallback, useMemo } from 'react';
import { isEqual } from 'lodash';
import {
  IValidationResult,
  useFlowBuilder2Store,
  TFlowBuilder2ObjectTypeFieldNamesMap,
} from '@diana/core';

export const useEntityFieldState = <T extends keyof TFlowBuilder2ObjectTypeFieldNamesMap>(args: {
  objectId: string;
  objectType: T;
  fieldName: TFlowBuilder2ObjectTypeFieldNamesMap[T];
}) => {
  const { objectId, objectType, fieldName } = args;

  const { touched, dirty, validationResult } = useFlowBuilder2Store((s) => {
    return s.getFieldState(objectId, objectType, fieldName);
  }, isEqual);
  const setFieldState = useFlowBuilder2Store((s) => s.setFieldState);

  const emitTouched = useCallback(() => {
    setFieldState(objectId, objectType, fieldName, (draft) => ({ ...draft, touched: true }));
  }, [setFieldState, objectId, objectType, fieldName]);

  const emitDirty = useCallback(() => {
    setFieldState(objectId, objectType, fieldName, (draft) => ({ ...draft, dirty: true }));
  }, [setFieldState, objectId, objectType, fieldName]);

  const setValidationResult = useCallback(
    (value: IValidationResult | null) => {
      setFieldState(objectId, objectType, fieldName, (draft) => ({
        ...draft,
        validationResult: value,
      }));
    },
    [setFieldState, objectId, objectType, fieldName],
  );

  return useMemo(
    () => ({
      touched,
      dirty,
      emitTouched,
      emitDirty,
      validationResult,
      setValidationResult,
    }),
    [touched, dirty, emitDirty, emitTouched, validationResult, setValidationResult],
  );
};
